<template>
  <div class="smart-hassan-id-card-report">
    <!-- Control Bar (Hidden on Print) -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <div class="brand-badge mr-2">
          <feather-icon icon="CreditCardIcon" size="20" class="text-white" />
        </div>
        <h4 class="mb-0 font-weight-bolder">
          {{ displaySchoolName }}
          <span class="text-primary">| Student ID Cards</span>
        </h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2 px-1">
          {{ reportItems.length }} Students Loaded
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print All Cards</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Exit</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5 loading-wrapper">
      <div class="loader-content">
        <b-spinner
          variant="danger"
          style="width: 4rem; height: 4rem"
          class="mb-3"
        />
        <h3 class="font-weight-bold text-danger">
          Generating HPGS Student ID Cards
        </h3>
        <p class="text-muted">
          Please wait while we process the student records...
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5 error-wrapper">
      <div class="error-icon-bg mb-3">
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-danger" />
      </div>
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button
        variant="outline-danger"
        @click="loadReportData"
        class="mt-3 round"
      >Try Again</b-button>
    </div>

    <!-- Cards Container -->
    <div v-else>
      <div
        v-if="reportItems.length === 0"
        class="text-center py-5 no-data-state"
      >
        <div class="no-data-icon">
          <feather-icon icon="InboxIcon" size="64" />
        </div>
        <h3 class="mt-2 font-weight-bold">No Students Found</h3>
        <p class="text-muted">
          Try adjusting your filters to find student records.
        </p>
      </div>

      <div v-else class="print-pages">
        <div
          v-for="(page, pageIndex) in cardPages"
          :key="'page-' + pageIndex"
          class="print-page"
          :class="`print-page--count-${page.length}`"
        >
          <div class="cards-grid">
            <div
              v-for="(student, index) in page"
              :key="student.id || student.grNo || `${pageIndex}-${index}`"
              class="student-id-block"
            >
              <template v-for="cardType in cardTypes">
              <!-- FRONT SIDE OF CARD -->
              <div
                :key="`front-${cardType.key}-${student.id || student.grNo || index}`"
                class="id-card id-card--front"
              >
                <div class="card-header-red card-header-red--front">
                  <div class="header-top-row header-top-row--logo-only">
                    <div class="tss-badge tss-badge--hpgs tss-badge--header-bar">
                      <img
                        :src="hpgsBackLogo"
                        alt="Happy Palace Group of Schools"
                        class="hpgs-header-logo hpgs-header-logo--brand"
                      />
                    </div>
                  </div>
                </div>

                <!-- Front Card Body -->
                <div class="card-body-front">
                  <!-- Student Photo Frame -->
                  <div class="photo-frame-wrapper">
                    <div class="photo-frame">
                      <img
                        :src="studentPhotoUrl(student)"
                        :alt="studentDisplayName(student)"
                        class="photo-frame__img"
                        referrerpolicy="no-referrer"
                        @error="handleImgError($event, student)"
                      />
                    </div>
                  </div>

                  <div class="front-summary">
                    <div class="front-student-name">
                      {{ studentDisplayName(student) }}
                    </div>
                    <div class="front-details-block">
                      <div class="front-class-block">
                        <div class="front-class-row front-class-row--cid">
                          <span class="front-class-key">{{ studentIdLabel }}:</span>
                          <span class="front-class-val front-class-val--cid">{{
                            getStudentCid(student)
                          }}</span>
                        </div>
                        <div class="front-class-row">
                          <span class="front-class-key">Class:</span>
                          <span class="front-class-val">{{
                            getStudentClass(student)
                          }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="barcodeCidValue(student)" class="front-barcode-wrap">
                    <div class="front-barcode-visual">
                      *{{ barcodeCidValue(student) }}*
                    </div>
                  </div>
                </div>

                <div class="card-front-footer">
                  <div class="card-footer-red card-footer-red--front">
                    <span class="footer-title">{{ cardType.label }}</span>
                  </div>
                </div>
              </div>

              <!-- BACK SIDE OF CARD -->
              <div
                :key="`back-${cardType.key}-${student.id || student.grNo || index}`"
                class="id-card id-card--back"
              >
                <div class="back-top-header">
                  <img
                    :src="hpgsBackLogo"
                    alt="Happy Palace Group of Schools"
                    class="back-hpgs-logo"
                  />
                </div>

                <div class="card-body-back">
                  <div class="back-student-meta">
                    <div class="back-meta-label">Father Name:</div>
                    <div class="back-meta-line back-meta-line--name">
                      <span class="back-meta-value back-meta-value--name">{{
                        studentFatherName(student)
                      }}</span>
                    </div>
                  </div>

                  <div class="back-return-info">
                    <div class="return-label">
                      If you found please return to:
                    </div>
                    <div class="return-campus-row">
                      <span class="return-campus-name">{{
                        backCampusLine || displaySchoolName
                      }}</span>
                    </div>

                    <div
                      v-if="displayCampusFacebook"
                      class="contact-entry contact-entry--back"
                    >
                      <div class="contact-icon">
                        <svg
                          viewBox="0 0 24 24"
                          class="fb-svg"
                          fill="#ED1D25"
                        >
                          <path
                            d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"
                          />
                        </svg>
                      </div>
                      <div class="contact-text">{{ displayCampusFacebook }}</div>
                    </div>

                    <div
                      v-if="displayCampusInstagram"
                      class="contact-entry contact-entry--back"
                    >
                      <div class="contact-icon">
                        <svg
                          viewBox="0 0 24 24"
                          class="insta-svg"
                          fill="#ED1D25"
                        >
                          <path
                            d="M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2m-.2 2A3.6 3.6 0 0 0 4 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6m9.65 1.5a1.25 1.25 0 0 1 1.25 1.25A1.25 1.25 0 0 1 17.25 8 1.25 1.25 0 0 1 16 6.75a1.25 1.25 0 0 1 1.25-1.25M12 7a5 5 0 0 1 5 5 5 5 0 0 1-5 5 5 5 0 0 1-5-5 5 5 0 0 1 5-5m0 2a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3z"
                          />
                        </svg>
                      </div>
                      <div class="contact-text">{{ displayCampusInstagram }}</div>
                    </div>

                    <div
                      v-if="displayCampusWebsite"
                      class="contact-entry contact-entry--back"
                    >
                      <div class="contact-icon">
                        <svg
                          viewBox="0 0 24 24"
                          class="web-svg"
                          fill="#ED1D25"
                        >
                          <path
                            d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zm6.93 6h-3.95c-.32-1.25-.78-2.45-1.38-3.56 1.84.63 3.37 1.91 4.33 3.56zM12 4.04c.83 1.2 1.48 2.53 1.91 3.96h-3.82c.43-1.43 1.08-2.76 1.91-3.96zM4.26 14C4.1 13.36 4 12.69 4 12s.1-1.36.26-2h3.38c-.08.66-.14 1.32-.14 2 0 .68.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.78 2.45 1.38 3.56-1.84-.63-3.37-1.91-4.33-3.56zm2.95-8H5.08c.96-1.66 2.49-2.93 4.33-3.56C8.81 5.55 8.35 6.75 8.03 8zM12 19.96c-.83-1.2-1.48-2.53-1.91-3.96h3.82c-.43 1.43-1.08 2.76-1.91 3.96zM14.34 14H9.66c-.09-.66-.16-1.32-.16-2 0-.68.07-1.34.16-2h4.68c.09.66.16 1.32.16 2 0 .68-.07 1.34-.16 2zm.25 5.56c.6-1.11 1.06-2.31 1.38-3.56h3.95c-.96 1.65-2.49 2.93-4.33 3.56zM16.36 14c.08-.66.14-1.32.14-2 0-.68-.06-1.34-.14-2h3.38c.16.64.26 1.31.26 2s-.1 1.36-.26 2h-3.38z"
                          />
                        </svg>
                      </div>
                      <div class="contact-text">{{ displayCampusWebsite }}</div>
                    </div>
                  </div>
                </div>

                <div class="bottom-waves-wrap bottom-waves-wrap--back">
                  <svg
                    class="bottom-waves-svg"
                    viewBox="0 0 204 32"
                    preserveAspectRatio="none"
                  >
                    <path
                      d="M0,18 Q60,30 130,12 T204,18 L204,32 L0,32 Z"
                      fill="#ED1D25"
                    />
                    <path
                      d="M0,24 Q70,12 140,26 T204,16 L204,32 L0,32 Z"
                      fill="#C91820"
                      opacity="0.85"
                    />
                  </svg>
                </div>
              </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import {
  getMediaPreviewUrl,
  handleDrivePreviewError,
} from "@/utils/gdriveUpload";

const HPGS_DEFAULT_WEBSITE = "https://hpgs.edu.pk/";
const HPGS_DEFAULT_INSTAGRAM = "http://instagram.com/hpgs_official/";
const HPGS_DEFAULT_FACEBOOK = "https://www.facebook.com/happypalacegroup";

function formatLinkForCard(raw) {
  let s = (raw || "").trim();
  if (!s) return "";
  s = s.replace(/^https?:\/\//i, "").replace(/^www\./i, "");
  if (!s.endsWith("/")) s += "/";
  return s;
}

export default {
  name: "HpgsIDCard",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      schoolName: "",
      campusName: "",
      campusAddress: "",
      campusPhone: "",
      campusEmail: "",
      campusFacebook: "",
      campusInstagram: "",
      campusWebsite: "",
      hpgsBackLogo: "/hpgs-logo-id%20card.png",
      db: "",
      cID: "",
      classID: 0,
      sectionID: 0,
      status: "present",
      sessYear: "",
      sessionID: 0,
      studentIDs: [],
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      cardTypes: [{ key: "student", label: "STUDENT ID CARD" }],
      defaultPlaceholder:
        "data:image/svg+xml," +
        encodeURIComponent(
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">' +
            '<rect width="120" height="120" fill="#eef1f4"/>' +
            '<circle cx="60" cy="44" r="26" fill="#9aa5b1"/>' +
            '<path d="M18 118c0-24 18.8-42 42-42s42 18 42 42" fill="#9aa5b1"/>' +
            "</svg>"
        ),
    };
  },
  computed: {
    displaySchoolName() {
      return (
        (this.schoolName || "").trim() || "Happy Palace Group of Schools"
      );
    },
    displayCampusName() {
      return (this.campusName || "").trim();
    },
    showHeaderCampusName() {
      const campus = this.displayCampusName;
      if (!campus) return false;
      return (
        campus.toLowerCase() !== this.displaySchoolName.toLowerCase()
      );
    },
    backCampusLine() {
      return this.showHeaderCampusName ? this.displayCampusName : "";
    },
    displayCampusAddress() {
      return (this.campusAddress || "").trim();
    },
    displayCampusEmail() {
      return (this.campusEmail || "").trim();
    },
    displayCampusFacebook() {
      const url =
        (this.campusFacebook || "").trim() || HPGS_DEFAULT_FACEBOOK;
      return formatLinkForCard(url);
    },
    displayCampusInstagram() {
      const url =
        (this.campusInstagram || "").trim() || HPGS_DEFAULT_INSTAGRAM;
      return formatLinkForCard(url);
    },
    displayCampusWebsite() {
      const url = (this.campusWebsite || "").trim() || HPGS_DEFAULT_WEBSITE;
      return formatLinkForCard(url);
    },
    studentIdLabel() {
      return "CID";
    },
    cardPages() {
      const pageSize = 2;
      const pages = [];
      const items = this.reportItems || [];

      for (let i = 0; i < items.length; i += pageSize) {
        pages.push(items.slice(i, i + pageSize));
      }

      return pages;
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData?.db;
      this.cID = query.cID || 0;
      this.classID = query.cls || "";
      this.sectionID = query.sec || "";
      if (query.studentIDs) {
        this.studentIDs = query.studentIDs
          .split(",")
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));
      }

      this.status =
        this.studentIDs.length > 0 ? "all" : query.status || "present";
      this.sessYear = query.sess || "2026-2027";
      this.sessionID = query.sessionID || 0;

      if (!this.db) {
        this.error = "System context missing. Please reload from dashboard.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = "https://apiv26.myskool.app/api/campuses";
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData?.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.schoolName =
              match.schoolName ||
              match.organizationName ||
              match.orgName ||
              "";
            this.campusName =
              match.branch || match.campusName || match.name || "";
            this.campusAddress =
              match.address || match.campusAddress || match.location || "";
            this.campusPhone =
              match.phone || match.contact || match.mobile || "";
            this.campusEmail = match.email || match.campusEmail || "";
            this.campusFacebook =
              match.facebook ||
              match.fb ||
              match.facebookUrl ||
              match.facebookPage ||
              "";
            this.campusInstagram =
              match.instagram ||
              match.insta ||
              match.instagramUrl ||
              match.instagramHandle ||
              "";
            this.campusWebsite =
              match.website || match.web || match.websiteUrl || "";
          }
        }
      } catch (e) {
        console.warn("Campus branding failed to load", e);
      }
    },
    getStudentClass(student) {
      if (!student) return "—";
      return (
        student.className ||
        student.cls ||
        student.meta_class ||
        "—"
      );
    },
    getStudentCampusDisplay() {
      const campus = (this.displayCampusName || "").trim();
      if (campus) return campus;
      const school = (this.displaySchoolName || "").trim();
      return school || "—";
    },
    /** HPGS: CID is stored in `grNo` (same as student grid). */
    getStudentCid(student) {
      if (!student) return "—";
      const raw =
        student.grNo ??
        student.cid ??
        student.CID ??
        student.studentCid ??
        "";
      if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
        return String(raw).trim();
      }
      return "—";
    },
    barcodeCidValue(student) {
      const cid = this.getStudentCid(student);
      return cid && cid !== "—" ? cid : "";
    },
    studentFatherName(student) {
      const name =
        student?.father_name || student?.fatherName || student?.father || "";
      return String(name).trim() || "—";
    },
    normalizeStudentList(responseData) {
      if (!responseData) return [];
      if (!Array.isArray(responseData) && Array.isArray(responseData.data)) {
        return responseData.data;
      }
      if (Array.isArray(responseData)) return responseData;
      return [];
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      const headers = {
        Authorization: `Bearer ${this.$store.state.userData?.token}`,
        accept: "application/json",
      };

      try {
        let response;

        if (this.studentIDs.length > 0) {
          const url = `${this.$store.state.domain}Students/LoadDataSelected`;
          response = await axios.get(url, {
            params: {
              db: this.db,
              cID: this.cID,
              ids: this.studentIDs.join(","),
              status: "all",
              sessionID: 0,
            },
            headers,
          });
        } else {
          const url = `${this.$store.state.domain}Students/LoadDataAll`;
          response = await axios.get(url, {
            params: {
              db: this.db,
              cID: this.cID,
              clsID: this.classID || 0,
              section: this.sectionID || 0,
              status: this.status,
              sessionID: 0,
            },
            headers,
          });
        }

        if (response.data && response.data.status === "success") {
          this.reportItems = this.normalizeStudentList(response.data.data);
        } else if (Array.isArray(response.data)) {
          this.reportItems = response.data;
        } else {
          this.error =
            response.data?.message ||
            "The server did not return valid student records.";
        }
      } catch (err) {
        console.error("Data retrieval error:", err);
        this.error = "Unable to connect to school records system.";
      } finally {
        this.loading = false;
      }
    },
    studentPhotoUrl(student) {
      const url = student?.picture || student?.photo || student?.image || "";
      if (!url) return this.defaultPlaceholder;
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    studentDisplayName(student) {
      const name =
        student?.name || student?.studentName || student?.student_name || "";
      return String(name).trim() || "—";
    },
    handleImgError(e, student) {
      const url = student?.picture || student?.photo || student?.image || "";
      if (url && !String(url).startsWith("blob:")) {
        handleDrivePreviewError(e, url, "image");
        return;
      }
      e.target.src = this.defaultPlaceholder;
    },
    print() {
      window.print();
    },
    close() {
      if (this.studentIDs && this.studentIDs.length > 0) {
        this.$router.push({ name: "manage-studentgrid" });
      } else {
        this.$router.push({ name: "report" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Alex+Brush&family=Dancing+Script:wght@700&family=Great+Vibes&family=Libre+Barcode+39&family=Montserrat:wght@400;500;600;700;800;900&display=swap");

$smart-red: #ed1d25;
$smart-red-dark: #c91820;
$smart-gold: #fdb813;
$text-dark: #1a1a1a;
$line-gray: #4a4a4a;
$card-w: 6.8cm;
$card-h: 9.5cm;

.smart-hassan-id-card-report {
  background: #eaedf2;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Montserrat", sans-serif;
  color: $text-dark;
}

/* Control Bar */
.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  padding: 1rem 2rem;
  margin-bottom: 2.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.6);

  .brand-badge {
    background: linear-gradient(135deg, $smart-red 0%, $smart-red-dark 100%);
    padding: 8px;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(157, 11, 15, 0.35);
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 0.7rem 1.6rem;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  border: none;
  outline: none;
  font-size: 0.88rem;

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, $smart-red 0%, $smart-red-dark 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(157, 11, 15, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(157, 11, 15, 0.45);
  }
}

.btn-close {
  background: white;
  color: #555;
  border: 1px solid #dcdde1;

  &:hover {
    background: #fff;
    color: #e74c3c;
    border-color: #fab1a0;
  }
}

/* Page & Grid Setup */
.print-pages {
  width: 100%;
}

.print-page + .print-page {
  margin-top: 32px;
}

.cards-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 24px 28px;
  width: 100%;
  margin: 0 auto;
}

.student-id-block {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 14px;
  width: auto;
  max-width: 100%;
  break-inside: avoid;
  page-break-inside: avoid;
}

/* Card Container */
.id-card {
  width: $card-w;
  height: $card-h;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  position: relative;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

/* =========================================
   CARD HEADER (SHARED FRONT & BACK)
   ========================================= */
.card-header-red {
  width: 100%;
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
  z-index: 2;

  &--front {
    background: #ffffff;
    height: auto;
    min-height: 58px;
    padding: 12px 5px 0;

    .header-top-row {
      position: relative;
      z-index: 2;
      padding-top: 4px;
    }
  }

  &--back {
    background: $smart-red;
    padding: 8px 8px 8px;
  }
}

$header-logo-w: 2.15cm;
$header-logo-slot-w: 0.55cm;
$header-logo-h: 32px;

.header-top-row {
  display: grid;
  grid-template-columns: $header-logo-w minmax(0, 1fr) $header-logo-slot-w;
  align-items: center;
  column-gap: 3px;
  width: 100%;

  &--logo-only {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 6px;
  }
}

/* Left campus logo badge */
.tss-badge {
  background: #ffffff;
  border-radius: 3px;
  padding: 1px 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: $header-logo-w;
  height: $header-logo-h;
  box-sizing: border-box;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  overflow: hidden;
  justify-self: start;

  &--hpgs {
    padding: 1px 3px;
  }

  &--header-bar {
    width: auto;
    max-width: 96%;
    min-width: 3.1cm;
    height: auto;
    min-height: 36px;
    padding: 0 4px;
    justify-self: center;
    background: transparent;
    box-shadow: none;
    border: none;

    .hpgs-header-logo {
      object-position: center center;
    }
  }

  .hpgs-header-logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: left center;
    display: block;

    &--brand {
      width: auto;
      height: 36px;
      max-width: 100%;
      object-position: center center;
    }
  }
}

.header-logo-slot {
  width: $header-logo-slot-w;
  height: $header-logo-h;
  justify-self: end;
  flex-shrink: 0;
}

/* Center: school + campus titles */
.header-center-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: 100%;
  padding: 0 1px;
  text-align: center;
}

.school-script-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 800;
  font-size: 7pt;
  line-height: 1.1;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: normal;
  letter-spacing: 0.02em;
  text-align: center;
  width: 100%;
  margin: 0 0 1px;
  text-transform: capitalize;
  word-break: break-word;
}

.campus-bar {
  display: block;
  width: 100%;
  padding: 0 1px;
  box-sizing: border-box;

  &__dash {
    display: none;
  }

  &__name {
    display: block;
    font-size: 5.35pt;
    font-weight: 500;
    color: #ffffff;
    white-space: normal;
    letter-spacing: 0.01em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    text-align: center;
    line-height: 1.15;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
}

/* Right City School Crest */
.city-crest {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 1.05cm;
  flex-shrink: 0;

  .city-crest-img {
    width: 27px;
    height: 28px;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
  }

  .crest-since {
    font-size: 3.8pt;
    font-weight: 600;
    color: #ffffff;
    white-space: nowrap;
    margin-top: 1.5px;
    letter-spacing: 0.02em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
    line-height: 1;
  }
}

/* =========================================
   CARD FRONT BODY & PHOTO
   ========================================= */
.card-body-front {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  padding: 6px 10px 4px;
  position: relative;
  z-index: 3;
  margin-top: 4px;
  overflow: hidden;
}

.photo-frame-wrapper {
  margin: 0;
  flex-shrink: 0;
}

.photo-frame {
  width: 2.6cm;
  height: 2.6cm;
  border-radius: 18px;
  border: 2.5px solid $smart-red;
  background: #eef1f4;
  overflow: hidden;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.front-summary {
  width: 100%;
  text-align: center;
  padding: 0 2px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.front-label {
  font-size: 6.2pt;
  font-weight: 800;
  color: #111111;
  line-height: 1.1;
  margin-bottom: 1px;

  &--gr {
    margin-top: 4px;
  }
}

.front-student-name {
  font-size: 11pt;
  font-weight: 900;
  color: $smart-red;
  line-height: 1.05;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0;
  word-break: break-word;
  text-align: center;
}

.front-details-block {
  width: 100%;
  text-align: center;
  padding: 0 8px;
  margin-top: 0;
  box-sizing: border-box;
}

.front-gr-no {
  font-size: 9pt;
  font-weight: 800;
  color: $smart-red;
  line-height: 1.1;
  margin-bottom: 4px;
}

.front-class-block {
  text-align: center;
  width: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.front-class-row {
  font-size: 7pt;
  font-weight: 800;
  color: #111111;
  line-height: 1.4;
  text-align: center;

  &--cid {
    margin-bottom: 0;
  }
}

.front-class-key {
  margin-right: 4px;
}

.front-class-val--cid {
  color: $smart-red;
}

.front-barcode-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 0;
  padding: 4px 8px 2px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.front-barcode-visual {
  font-family: "Libre Barcode 39", cursive;
  font-size: 28pt;
  color: #000000;
  letter-spacing: 1px;
  line-height: 0.75;
  max-width: 100%;
  overflow: hidden;
  text-align: center;
}

.id-card--front .card-front-footer {
  width: 100%;
  flex: 0 0 auto;
  flex-shrink: 0;
  margin-top: 0;
  margin-bottom: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 5;
}

.id-card--front .card-footer-red--front {
  height: 36px;
  min-height: 36px;
  max-height: 36px;
  margin-top: 0;
  box-sizing: border-box;
  padding: 0 8px;
  border-radius: 0 0 14px 14px;

  .footer-title {
    font-size: 9pt;
    line-height: 1;
  }
}

/* =========================================
   BOTTOM WAVES & FOOTER (FRONT & BACK)
   ========================================= */
.bottom-waves-wrap {
  width: 100%;
  height: 18px;
  margin-top: auto;
  position: relative;
  z-index: 1;

  .bottom-waves-svg {
    width: 100%;
    height: 100%;
    display: block;
  }
}

.card-footer-red {
  background: $smart-red;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  flex-shrink: 0;
  position: relative;
  z-index: 2;

  .footer-dash {
    height: 1.5px;
    background: #ffffff;
    width: 32px;
    opacity: 0.9;
  }

  .footer-title {
    font-size: 9.5pt;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 0.08em;
  }

  &--back {
    height: auto;
    min-height: 22px;
    flex-direction: column;
    justify-content: center;
    padding: 4px 0;

    .back-moto {
      font-size: 8.2pt;
      font-weight: 800;
      color: #ffffff;
      letter-spacing: 0.06em;
      line-height: 1.15;
    }
  }
}

/* =========================================
   CARD BACK BODY
   ========================================= */
.id-card--back {
  .back-top-header {
    width: 100%;
    background: #ffffff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px 8px 6px;
    box-sizing: border-box;
  }

  .back-hpgs-logo {
    height: 34px;
    width: auto;
    max-width: 96%;
    object-fit: contain;
    object-position: center center;
    display: block;
  }

  .bottom-waves-wrap--back {
    margin-top: auto;
    margin-bottom: 0;
    height: 32px;
    width: 100%;
    line-height: 0;
    flex-shrink: 0;
    position: relative;
    z-index: 2;

    .bottom-waves-svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }
}

.card-body-back {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 14px 14px;
  position: relative;
  z-index: 3;
  min-height: 0;
  gap: 20px;
  text-align: center;
}

.back-student-meta {
  width: 100%;
  text-align: center;
  margin-bottom: 0;
  flex-shrink: 0;
}

.back-meta-label {
  font-size: 7pt;
  font-weight: 800;
  color: #111111;
  text-align: center;
  line-height: 1.25;
  margin-bottom: 5px;
}

.back-meta-line {
  line-height: 1.4;
  margin-bottom: 8px;
  font-size: 7pt;
  font-weight: 800;
  color: #111111;
  text-align: center;

  &--name {
    margin-bottom: 10px;
  }

  &:last-child {
    margin-bottom: 0;
  }
}

.back-meta-value {
  font-size: inherit;
  font-weight: inherit;
  color: inherit;

  &--name {
    text-transform: uppercase;
    color: $smart-red;
  }

}

/* Back Return Info */
.back-return-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: 0;
  padding: 0 4px;
  text-align: center;
  flex-shrink: 0;
  justify-content: center;
  gap: 8px;
}

.return-label {
  font-size: 7pt;
  font-weight: 800;
  color: #111111;
  margin-bottom: 0;
}

.return-campus-row {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 0;
  padding: 0 4px;
  box-sizing: border-box;
}

.return-campus-name {
  font-size: 8.2pt;
  font-weight: 800;
  color: $smart-red;
  text-align: center;
  line-height: 1.2;
  word-break: break-word;
}

.contact-entry--back {
  align-items: center;
  justify-content: center;
  padding-left: 0;
  margin-bottom: 0;
  gap: 6px;
  width: 100%;

  .contact-icon {
    margin-top: 1px;

    .loc-pin-svg,
    .phone-svg,
    .mail-svg,
    .web-svg,
    .fb-svg,
    .insta-svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  .contact-text {
    font-size: 6.1pt;
    font-weight: 700;
    line-height: 1.25;
    text-align: left;
    word-break: break-word;
  }
}

.campus-main-entry {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin-bottom: 5px;
  width: 100%;
  box-sizing: border-box;

  &--school {
    margin-bottom: 3px;
  }

  &--campus {
    justify-content: center;
    flex-wrap: nowrap;
    gap: 4px;
    padding: 0 4px;
    max-width: 100%;
  }

  .campus-main-pin {
    flex-shrink: 0;
    color: $smart-red;
    stroke-width: 2.5px;
  }

  .campus-main-title {
    font-size: 11pt;
    font-weight: 900;
    color: $smart-red;
    letter-spacing: -0.02em;
    line-height: 1.1;
    white-space: normal;
    text-align: center;
    width: 100%;
    padding: 0 4px;
  }

  .campus-line-name {
    font-size: 7.2pt;
    font-weight: 500;
    color: $smart-red;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left;
    flex: 0 1 auto;
    max-width: calc(100% - 20px);
  }
}

.campus-address-entry {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 5px;
  padding: 0 6px;
  margin-bottom: 5px;

  .pin-icon-small {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
    margin-top: 1px;

    .loc-pin-svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  .address-text {
    font-size: 6.4pt;
    font-weight: 700;
    color: #222222;
    line-height: 1.25;
    text-align: left;
  }
}

.back-divider-line {
  width: 90%;
  height: 1.2px;
  background: $line-gray;
  margin: 5px 0 7px;
}

.contact-entry {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding-left: 10px;
  margin-bottom: 4px;

  .contact-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;

    .mail-svg,
    .web-svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  .contact-text {
    font-size: 6.8pt;
    font-weight: 700;
    color: #222222;
    letter-spacing: 0.01em;
  }
}

/* Loading, Error & Empty States */
.loading-wrapper,
.error-wrapper,
.no-data-state {
  background: white;
  border-radius: 20px;
  padding: 4rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

/* =========================================
   PRINT STYLES
   ========================================= */
@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  @page {
    size: A4 landscape;
    margin: 5mm 4mm 4mm 4mm;
  }

  html,
  body,
  #app,
  #app.h-100 {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .smart-hassan-id-card-report {
    padding: 0 !important;
    background: white !important;
    min-height: 0 !important;
    height: auto !important;
  }

  .print-pages {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
  }

  .print-page {
    width: 100% !important;
    height: auto !important;
    padding-top: 2mm !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  .print-page:not(:last-child) {
    page-break-after: always !important;
    break-after: page !important;
  }

  .print-page:last-child {
    page-break-after: auto !important;
    break-after: auto !important;
  }

  .print-page + .print-page {
    margin-top: 0 !important;
  }

  .cards-grid {
    display: flex !important;
    flex-wrap: wrap !important;
    justify-content: center !important;
    align-content: start !important;
    width: 100% !important;
    margin: 0 auto !important;
    padding: 0 !important;
    gap: 3mm 4mm !important;
  }

  .student-id-block {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: wrap !important;
    align-items: center !important;
    justify-content: center !important;
    width: auto !important;
    max-width: 100% !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    gap: 2mm !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .id-card {
    width: $card-w !important;
    height: $card-h !important;
    min-width: $card-w !important;
    min-height: $card-h !important;
    max-width: $card-w !important;
    max-height: $card-h !important;
    flex-shrink: 0 !important;
    border-radius: 8px !important;
    overflow: hidden !important;
    box-shadow: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .card-header-red,
  .card-footer-red,
  .tss-badge,
  .city-crest,
  .field-icon-box,
  .photo-frame,
  .stripes-left .stripe-line,
  .stripes-right .stripe-line,
  .book-emblem-outer,
  .book-emblem-inner,
  .bottom-waves-wrap {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>

<style lang="scss">
@media print {
  html,
  body,
  #app,
  #app.h-100 {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }
}
</style>

